import React from 'react';
import { Box, Grid, IconButton, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import MobileButton from '@components/MobileButton';
import type { ClientInput } from './pricingCalculatorTypes';

interface CondominioClientsListProps {
  clients: ClientInput[];
  allowMultipleClients: boolean;
  calculateClientPrice: (units: number) => number;
  formatCurrency: (value: number) => string;
  onUnitsChange: (clientId: string, value: string) => void;
  onAddClient: () => void;
  onRemoveClient: (clientId: string) => void;
}

/**
 * Lista de condomínios (unidades + preço calculado) do plano Condomínio, com opção de adicionar
 * múltiplos condomínios. Extraído de UnifiedPricingCalculator.tsx (F7).
 */
export const CondominioClientsList: React.FC<CondominioClientsListProps> = ({
  clients,
  allowMultipleClients,
  calculateClientPrice,
  formatCurrency,
  onUnitsChange,
  onAddClient,
  onRemoveClient,
}) => {
  return (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
        Condomínios
      </Typography>
      {clients.map((client, index) => {
        const units = parseInt(client.units, 10) || 0;
        const price = calculateClientPrice(units);
        return (
          <Box key={client.id} sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  Condomínio {index + 1}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={allowMultipleClients ? 6 : 7}>
                <TextField
                  fullWidth
                  label="Unidades"
                  type="text"
                  value={client.units}
                  onChange={(e) => onUnitsChange(client.id, e.target.value)}
                  placeholder="Ex: 200"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={allowMultipleClients ? 2 : 4}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 'bold',
                    color: units > 0 ? 'primary.main' : 'text.secondary',
                    textAlign: { xs: 'left', sm: 'right' },
                  }}
                >
                  {units > 0 ? formatCurrency(price) : '-'}
                </Typography>
              </Grid>
              {allowMultipleClients && (
                <Grid item xs={12} sm={1}>
                  {clients.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => onRemoveClient(client.id)}
                      color="error"
                      sx={{ display: { xs: 'block', sm: 'flex' } }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        );
      })}
      {allowMultipleClients && (
        <MobileButton
          variant="outlined"
          icon={<Add />}
          onClick={onAddClient}
          tooltipTitle="Adicionar condomínio"
          color="primary"
          sx={{ mt: 1 }}
        >
          Adicionar condomínio
        </MobileButton>
      )}
    </Grid>
  );
};

export default CondominioClientsList;
